import React, { useState, useEffect } from 'react';
import { APP_INFO } from '../constants';
import { Input } from '../components/Input';
import { validateCPF, formatCPF, validateCEP, formatCEP } from '../services/validation';
import { 
  LogIn, 
  AlertCircle, 
  Loader2, 
  ChevronLeft, 
  ShieldCheck,
  CheckCircle2,
  Camera,
  ChevronRight,
  Mail,
  RefreshCw,
  Lock,
  XCircle,
  Search,
  KeyRound
} from 'lucide-react';
import { supabase } from '../services/supabase';
import { storage } from '../services/storage';
import { t } from '../services/i18n';
import { fileToBase64Compressed } from '../services/imageUtils';
import { Eraser } from 'lucide-react';
import { translateAuthError } from '../services/authErrors';

interface AuthProps {
  onLogin: () => void;
  initialMode?: 'login' | 'register' | 'recovery';
}

export const Auth: React.FC<AuthProps> = ({ onLogin, initialMode = 'login' }) => {
  const [mode, setMode] = useState<'login' | 'register' | 'forgot' | 'new-password'>(
    initialMode === 'recovery' ? 'new-password' : 'login'
  );
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [isFetchingCep, setIsFetchingCep] = useState(false);

  // Auth Inputs
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Profile Inputs
  const [nome, setNome] = useState('');
  const [idade, setIdade] = useState('');
  const [telefone, setTelefone] = useState('');
  const [cpf, setCpf] = useState('');
  const [endereco, setEndereco] = useState('');
  const [cidade, setCidade] = useState('');
  const [estado, setEstado] = useState('');
  const [cep, setCep] = useState('');
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    setSuccessMsg(null);
    if (initialMode === 'recovery') {
      setMode('new-password');
    }
  }, [initialMode]);

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCPF(e.target.value);
    setCpf(formatted);
  };

  const handleCepChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCEP(e.target.value);
    setCep(formatted);
    
    const cleanCep = formatted.replace(/\D/g, '');
    if (cleanCep.length === 8) {
      handleCepLookup(cleanCep);
    }
  };

  const isCpfValid = cpf ? validateCPF(cpf) : true;
  const isCepValid = cep ? validateCEP(cep) : true;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password });
      if (authError) throw authError;
      if (data.user) {
        storage.ensureUserExists(data.user).catch(err => console.error("Profile sync error:", err));
        onLogin();
      }
    } catch (err: any) {
      setError(translateAuthError(err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      const { error: authError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin
        }
      });
      if (authError) throw authError;
    } catch (err: any) {
      setError(translateAuthError(err.message));
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError("Por favor, informe seu e-mail.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/#/auth?recovery=true`,
      });
      if (resetError) throw resetError;
      setSuccessMsg("Link de recuperação enviado para o seu e-mail!");
    } catch (err: any) {
      setError(translateAuthError(err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError("As senhas não coincidem.");
      return;
    }
    if (password.length < 6) {
      setError("A senha deve ter pelo menos 6 caracteres.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      setSuccessMsg("Senha atualizada com sucesso! Você já pode entrar.");
      setMode('login');
    } catch (err: any) {
      setError(translateAuthError(err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError("As senhas não coincidem.");
      return;
    }
    if (cpf && !validateCPF(cpf)) {
      setError("CPF informado é inválido.");
      return;
    }
    if (cep && !validateCEP(cep)) {
      setError("CEP informado é inválido.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin,
          data: { 
            full_name: nome, 
            nome,
            idade,
            telefone,
            cpf,
            endereco,
            cidade,
            estado,
            cep,
            photoUrl,
            avatar_url: photoUrl 
          }
        }
      });
      
      if (authError) throw authError;
      
      if (data.user) {
        const extraData = { nome, idade, telefone, cpf, endereco, cidade, estado, cep, photoUrl };
        if (data.session) {
          await storage.ensureUserExists(data.user, extraData).catch(console.error);
          onLogin();
        } else {
          setSuccessMsg("Verifique seu e-mail para confirmar o cadastro!");
          setMode('login');
        }
      }
    } catch (err: any) {
      setError(translateAuthError(err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCepLookup = async (cleanCep: string) => {
    setIsFetchingCep(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setEndereco(data.logradouro || '');
        setCidade(data.localidade || '');
        setEstado(data.uf || '');
      }
    } catch (err) { console.error(err); }
    finally { setIsFetchingCep(false); }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLoading(true);
      try {
        const compressedBase64 = await fileToBase64Compressed(file);
        setPhotoUrl(compressedBase64);
      } catch (err) {
        console.error("Erro ao processar foto:", err);
        setError("Erro ao processar foto.");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-raizama-bg flex flex-col items-center justify-center p-6 max-w-md mx-auto animate-fade-in">
      <div className="mb-8 flex flex-col items-center text-center">
        <img src={APP_INFO?.LOGO_URL || ''} alt="Raizama" className="w-20 h-20 mb-4 drop-shadow-2xl" />
        <h1 className="text-3xl font-black text-raizama-action tracking-tighter">Raizama Garden CARE</h1>
        <p className="text-raizama-textSec italic text-xs font-bold opacity-70 mt-1">{APP_INFO.SLOGAN}</p>
      </div>

      <div className="w-full bg-raizama-surface/60 border border-raizama-border/20 p-8 rounded-[2.5rem] shadow-2xl backdrop-blur-sm">
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-2xl text-red-400 text-xs flex items-center gap-2 animate-scale-up">
            <AlertCircle size={18} /> {error}
          </div>
        )}
        {successMsg && (
          <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl text-emerald-400 text-xs flex items-center gap-2 animate-scale-up">
            <CheckCircle2 size={18} /> {successMsg}
          </div>
        )}

        <div className="flex flex-col items-center mb-8 gap-2">
          <h2 className="text-sm font-semibold text-white uppercase tracking-[0.2em] text-center opacity-80">
            {mode === 'login' ? t('auth_welcome') : mode === 'register' ? t('auth_register') : mode === 'forgot' ? t('auth_forgot') : t('auth_new_password')}
          </h2>
          {mode === 'register' && (
            <span className="text-[9px] font-black text-raizama-action bg-raizama-action/10 px-3 py-1 rounded-full border border-raizama-action/20 uppercase tracking-widest">
              {t('auth_step')} {step} {t('auth_of')} 3
            </span>
          )}
        </div>

        {mode === 'login' && (
          <div className="space-y-5">
            <form onSubmit={handleLogin} className="space-y-5">
              <Input label={t('auth_email_label')} type="email" value={email} onChange={e => setEmail(e.target.value)} required />
              <Input label={t('auth_password_label')} type="password" value={password} onChange={e => setPassword(e.target.value)} required />
              
              <div className="flex justify-end">
                <button 
                  type="button" 
                  onClick={() => setMode('forgot')}
                  className="text-[10px] text-raizama-textSec font-bold hover:text-raizama-action uppercase tracking-widest"
                >
                  {t('auth_forgot_link')}
                </button>
              </div>

              <button disabled={loading} className="w-full bg-raizama-action text-white font-black py-4 rounded-2xl shadow-xl uppercase tracking-widest text-sm transition-all flex items-center justify-center gap-2">
                {loading ? <Loader2 className="animate-spin" /> : <LogIn size={20} />}
                {t('auth_login_btn')}
              </button>
            </form>

            <button 
              type="button"
              onClick={handleGoogleLogin}
              disabled={loading}
              className="w-full bg-white/5 border border-raizama-border/30 text-white font-bold py-4 rounded-2xl shadow-lg flex items-center justify-center gap-3 transition-all hover:bg-white/10 mt-4"
            >
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-5 h-5" alt="Google" />
              {t('auth_google_btn')}
            </button>

            <button type="button" onClick={() => setMode('register')} className="w-full text-xs text-raizama-textSec font-bold pt-4 transition-colors">
              {t('auth_no_account')} <span className="text-raizama-action hover:text-raizama-highlight font-black">{t('auth_register_link')}</span>
            </button>

            <div className="pt-8 border-t border-raizama-border/10 mt-8">
              <button 
                type="button"
                onClick={() => {
                  if (confirm("Isso irá limpar todos os dados do navegador e pode resolver erros de 'Quota Exceeded'. Deseja continuar?")) {
                    localStorage.clear();
                    sessionStorage.clear();
                    window.location.reload();
                  }
                }}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-raizama-textSec/20 text-raizama-textSec/60 hover:text-raizama-action transition-all text-[9px] font-black uppercase tracking-widest"
              >
                <Eraser size={12} />
                Limpar Cache de Emergência
              </button>
            </div>
          </div>
        )}

        {mode === 'forgot' && (
          <div className="space-y-6">
            <p className="text-xs text-raizama-textSec text-center leading-relaxed">
              {t('auth_forgot')}
            </p>
            <form onSubmit={handleForgotPassword} className="space-y-5">
              <Input label={t('auth_email_label')} type="email" value={email} onChange={e => setEmail(e.target.value)} required />
              <button disabled={loading} className="w-full bg-raizama-action text-white font-black py-4 rounded-2xl shadow-xl uppercase tracking-widest text-sm transition-all flex items-center justify-center gap-2">
                {loading ? <Loader2 className="animate-spin" /> : <RefreshCw size={20} />}
                {t('auth_send_link')}
              </button>
            </form>
            <button 
              type="button" 
              onClick={() => setMode('login')} 
              className="w-full flex items-center justify-center gap-2 text-xs text-raizama-textSec font-bold hover:text-raizama-action transition-colors"
            >
              <ChevronLeft size={16} /> {t('auth_back_login')}
            </button>
          </div>
        )}

        {mode === 'new-password' && (
          <div className="space-y-6">
            <p className="text-xs text-raizama-textSec text-center leading-relaxed">
              {t('auth_new_password')}
            </p>
            <form onSubmit={handleUpdatePassword} className="space-y-5">
              <Input label={t('auth_password_label')} type="password" value={password} onChange={e => setPassword(e.target.value)} required />
              <Input label={t('auth_confirm_password_label')} type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required />
              <button disabled={loading} className="w-full bg-raizama-action text-white font-black py-4 rounded-2xl shadow-xl uppercase tracking-widest text-sm transition-all flex items-center justify-center gap-2">
                {loading ? <Loader2 className="animate-spin" /> : <KeyRound size={20} />}
                {t('auth_reset_btn')}
              </button>
            </form>
          </div>
        )}

        {mode === 'register' && (
          <div className="space-y-6">
            {step === 1 && (
              <div className="space-y-5">
                <div className="flex flex-col items-center mb-4">
                  <label className="relative w-20 h-20 rounded-full border-2 border-raizama-action overflow-hidden cursor-pointer flex items-center justify-center bg-black/20">
                    {photoUrl ? <img src={photoUrl} className="w-full h-full object-cover" alt="Profile" /> : <Camera className="text-raizama-textSec" />}
                    <input type="file" className="hidden" onChange={handlePhotoUpload} />
                  </label>
                </div>
                <Input label={t('auth_name_label')} value={nome} onChange={e => setNome(e.target.value)} required />
                <div className="grid grid-cols-2 gap-4">
                  <Input label={t('auth_age_label')} value={idade} onChange={e => setIdade(e.target.value)} type="number" />
                  <Input label={t('auth_phone_label')} value={telefone} onChange={e => setTelefone(e.target.value)} type="tel" />
                </div>
                <Input 
                  label={t('auth_cpf_label')} 
                  value={cpf} 
                  onChange={handleCpfChange} 
                  maxLength={14}
                  inputMode="numeric"
                  placeholder="000.000.000-00"
                  rightElement={cpf && (isCpfValid ? <CheckCircle2 size={16} className="text-emerald-500" /> : <XCircle size={16} className="text-red-500" />)}
                />
                <button 
                  onClick={() => setStep(2)} 
                  disabled={!nome || !isCpfValid} 
                  className="w-full bg-raizama-action text-white font-black py-4 rounded-2xl shadow-lg flex items-center justify-center gap-2 uppercase tracking-widest text-xs disabled:opacity-30"
                >
                  {t('auth_continue')} <ChevronRight size={18} />
                </button>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5">
                <Input 
                  label={t('auth_cep_label')} 
                  value={cep} 
                  onChange={handleCepChange} 
                  maxLength={9} 
                  inputMode="numeric"
                  placeholder="00000-000"
                  rightElement={
                    isFetchingCep ? <Loader2 size={16} className="animate-spin text-raizama-action" /> :
                    cep && (isCepValid ? <CheckCircle2 size={16} className="text-emerald-500" /> : <XCircle size={16} className="text-red-500" />)
                  }
                />
                <Input label={t('auth_address_label')} value={endereco} onChange={e => setEndereco(e.target.value)} />
                <div className="grid grid-cols-2 gap-4">
                  <Input label={t('auth_city_label')} value={cidade} onChange={e => setCidade(e.target.value)} />
                  <Input label={t('auth_state_label')} value={estado} onChange={e => setEstado(e.target.value.toUpperCase())} maxLength={2} placeholder="UF" />
                </div>
                <div className="flex gap-4">
                  <button onClick={() => setStep(1)} className="flex-1 bg-white/5 text-white py-4 rounded-2xl border border-white/10 uppercase text-[10px]">{t('auth_back')}</button>
                  <button onClick={() => setStep(3)} disabled={!isCepValid} className="flex-1 bg-raizama-action text-white font-black py-4 rounded-2xl uppercase text-[10px] tracking-widest shadow-lg disabled:opacity-30">{t('auth_advance')}</button>
                </div>
              </div>
            )}

            {step === 3 && (
              <form onSubmit={handleRegister} className="space-y-5">
                <Input label={t('auth_email_label')} type="email" value={email} onChange={e => setEmail(e.target.value)} required />
                <Input label={t('auth_password_label')} type="password" value={password} onChange={e => setPassword(e.target.value)} required />
                <Input label={t('auth_confirm_password_label')} type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required />
                <div className="flex gap-4">
                  <button type="button" onClick={() => setStep(2)} className="flex-1 bg-white/5 text-white py-4 rounded-2xl border border-white/10 uppercase text-[10px]">{t('auth_back')}</button>
                  <button type="submit" disabled={loading} className="flex-1 bg-raizama-action text-white font-black py-4 rounded-2xl flex items-center justify-center gap-2 uppercase text-[10px] tracking-widest shadow-lg">
                    {loading ? <Loader2 className="animate-spin" /> : t('auth_register_btn')}
                  </button>
                </div>
              </form>
            )}
            
            <button 
              type="button" 
              onClick={() => setMode('login')} 
              className="w-full text-xs text-raizama-textSec font-bold hover:text-white transition-colors"
            >
              {t('auth_already_account')} {t('auth_login_link')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
